import React, { useState, useCallback } from 'react';
import { CheckCircle2, Copy, Check, ChevronDown, ChevronUp, TrendingUp, Users } from 'lucide-react';
import Button from '../../common/Button/Button';
import type { FoundContact } from '../../../types/results.types';

export interface FindMoreCompany {
  name: string;
  count: number;
}

interface EnrichmentResultsProps {
  successCount: number;
  failedCount: number;
  totalCount: number;
  foundContacts?: FoundContact[];
  onFindMore?: () => void;
  findMoreContactCount?: number;
  findMoreCompanyCount?: number;
  findMoreCompanies?: FindMoreCompany[];
  findMoreLoading?: boolean;
  onUpgradeClick?: () => void;
  onClose: () => void;
}

function ContactInitials({ name }: { name: string }) {
  const parts = name.trim().split(/\s+/);
  const initials = parts.length >= 2
    ? `${parts[0][0]}${parts[parts.length - 1][0]}`
    : parts[0].slice(0, 2);
  return <>{initials.toUpperCase()}</>;
}

const AVATAR_HUES = ['#059669', '#2563EB', '#7C3AED', '#D97706', '#DC2626'];

const INITIAL_VISIBLE = 3;

function CopyEmailButton({ email }: { email: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(email).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [email]);

  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); handleCopy(); }}
      className="flex shrink-0 items-center justify-center rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
      title={copied ? 'Copied!' : 'Copy email'}
      aria-label={copied ? 'Copied' : 'Copy email'}
    >
      {copied
        ? <Check className="h-3.5 w-3.5 text-emerald-600" strokeWidth={2.5} />
        : <Copy className="h-3.5 w-3.5" strokeWidth={2} />
      }
    </button>
  );
}

const EnrichmentResults: React.FC<EnrichmentResultsProps> = ({
  successCount,
  failedCount,
  totalCount,
  foundContacts = [],
  onFindMore,
  findMoreContactCount = 0,
  findMoreCompanyCount = 0,
  findMoreCompanies = [],
  findMoreLoading = false,
  onUpgradeClick,
  onClose,
}) => {
  const [expanded, setExpanded] = useState(false);

  const hasFound = successCount > 0;
  const extraContacts = Math.max(0, foundContacts.length - INITIAL_VISIBLE);
  const visibleContacts = expanded ? foundContacts : foundContacts.slice(0, INITIAL_VISIBLE);

  const hasFindMoreNudge = Boolean(onFindMore) && findMoreContactCount > 0;
  const hasUpgradeNudge = Boolean(onUpgradeClick) && findMoreContactCount > 0 && !hasFindMoreNudge;
  const hasAnyNudge = hasFindMoreNudge || hasUpgradeNudge || findMoreLoading;

  const sortedChipCompanies = [...findMoreCompanies].sort((a, b) => b.count - a.count);
  const chipCompanies = sortedChipCompanies.slice(0, 2);
  const extraChipCompanies = Math.max(0, sortedChipCompanies.length - 2);

  return (
    <div className="space-y-4 pb-1 pt-2">
      {/* Headline */}
      <div className="flex items-start gap-3">
        <div
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border-2 ${
            hasFound ? 'border-emerald-200 bg-emerald-50' : 'border-gray-200 bg-gray-50'
          }`}
        >
          <CheckCircle2
            className={`h-5 w-5 ${hasFound ? 'text-emerald-600' : 'text-gray-400'}`}
            strokeWidth={2.5}
          />
        </div>
        <div className="min-w-0 pt-0.5">
          <h3 className="text-base font-extrabold tracking-tight text-gray-900">
            {hasFound
              ? `${successCount.toLocaleString()} owner email${successCount !== 1 ? 's' : ''} found`
              : 'No owner emails found'}
          </h3>
          <p className="mt-1 text-xs leading-snug text-gray-500">
            {hasFound
              ? 'Ready to use — no extra verification step needed'
              : `Searched ${totalCount.toLocaleString()} contact${totalCount !== 1 ? 's' : ''} · nothing found this time`}
          </p>
        </div>
      </div>

      {/* Stats pills — found + not found = processed (honest numbers) */}
      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-2 py-3 text-center">
          <div className={`text-2xl font-extrabold ${hasFound ? 'text-emerald-600' : 'text-gray-300'}`}>
            {successCount.toLocaleString()}
          </div>
          <div className="mt-1 text-[10.5px] font-medium text-gray-400">Emails found</div>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white px-2 py-3 text-center">
          <div className={`text-2xl font-extrabold ${failedCount > 0 ? 'text-gray-700' : 'text-gray-300'}`}>
            {failedCount.toLocaleString()}
          </div>
          <div className="mt-1 text-[10.5px] font-medium text-gray-400">Not found</div>
        </div>
        <div className="rounded-xl border border-[#EDE9E3] bg-white px-2 py-3 text-center">
          <div className="text-2xl font-extrabold text-gray-900">{totalCount.toLocaleString()}</div>
          <div className="mt-1 text-[10.5px] font-medium text-gray-400">Processed</div>
        </div>
      </div>

      {/* Found contacts list — copyable, expandable, scroll-contained */}
      {foundContacts.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs font-semibold text-gray-700">Owner emails found</p>

          <div className={`space-y-2 ${expanded && extraContacts > 0 ? 'max-h-64 overflow-y-auto pr-0.5' : ''}`}>
            {visibleContacts.map((contact, index) => (
              <div
                key={`${contact.email}-${index}`}
                className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-[#F0FDF9] px-3 py-2.5"
              >
                <div
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white"
                  style={{ background: AVATAR_HUES[index % AVATAR_HUES.length] }}
                >
                  <ContactInitials name={contact.name} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold leading-tight text-gray-900">{contact.name}</p>
                  <p className="text-[11px] text-gray-500">{contact.role}</p>
                </div>
                <div className="flex shrink-0 items-center gap-1 rounded-md border-[1.5px] border-emerald-200 bg-white px-2 py-1 max-w-[180px]">
                  <svg
                    width="11" height="11" viewBox="0 0 24 24" fill="none"
                    stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    className="shrink-0"
                  >
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                  </svg>
                  <span className="truncate font-mono text-[11px] font-bold tracking-tight text-emerald-700">
                    {contact.email}
                  </span>
                  <CopyEmailButton email={contact.email} />
                </div>
              </div>
            ))}
          </div>

          {extraContacts > 0 && (
            <button
              type="button"
              onClick={() => setExpanded((prev) => !prev)}
              className="flex w-full items-center justify-center gap-1.5 py-1.5 text-xs font-semibold text-emerald-600 transition-colors hover:text-emerald-700"
            >
              {expanded
                ? <><ChevronUp className="h-3.5 w-3.5" /> Show less</>
                : <><ChevronDown className="h-3.5 w-3.5" /> Show {extraContacts} more email{extraContacts !== 1 ? 's' : ''}</>
              }
            </button>
          )}
        </div>
      )}

      {/* Not-found trust note */}
      {failedCount > 0 && (
        <p className="text-center text-xs font-medium leading-snug text-gray-400">
          {failedCount.toLocaleString()} contact{failedCount !== 1 ? 's' : ''} couldn&apos;t be found
          {' '}·{' '}
          <span className="text-gray-500">you&apos;re not charged for those</span>
        </p>
      )}

      {/* Find-more / upgrade nudge slot */}
      {findMoreLoading && (
        <div className="h-[100px] animate-pulse rounded-xl border border-gray-200 bg-gray-50" />
      )}

      {!findMoreLoading && hasFindMoreNudge && (
        <div className="space-y-3">
          <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-3.5">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-bold leading-snug text-gray-900">
                  <span className="text-xl font-extrabold">{findMoreContactCount.toLocaleString()}</span>
                  {' '}more contacts waiting
                </p>
                <p className="mt-0.5 text-[11px] leading-snug text-gray-500">
                  Across {findMoreCompanyCount} {findMoreCompanyCount === 1 ? 'company' : 'companies'} · you pick which before any credits are used
                </p>
              </div>
              <div className="flex shrink-0 items-center">
                {sortedChipCompanies.slice(0, 3).map((company, index) => (
                  <div
                    key={company.name}
                    className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-white text-[10px] font-bold text-white"
                    style={{
                      background: AVATAR_HUES[index % AVATAR_HUES.length],
                      marginLeft: index === 0 ? 0 : '-8px',
                      zIndex: 3 - index,
                      position: 'relative',
                    }}
                    title={company.name}
                  >
                    {company.name.slice(0, 2).toUpperCase()}
                  </div>
                ))}
                {extraChipCompanies > 0 && (
                  <div
                    className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-gray-400 text-[10px] font-bold text-white"
                    style={{ marginLeft: '-8px', position: 'relative', zIndex: 0 }}
                  >
                    +{extraChipCompanies}
                  </div>
                )}
              </div>
            </div>
          </div>
          <Button onClick={onFindMore} variant="primary" className="w-full flex items-center justify-center gap-2">
            Find more owner emails
            <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
            </svg>
          </Button>
          <button
            type="button"
            onClick={onClose}
            className="w-full py-1.5 text-sm font-medium text-gray-400 transition-colors hover:text-gray-600"
          >
            Maybe later
          </button>
        </div>
      )}

      {!findMoreLoading && hasUpgradeNudge && (
        <div className="space-y-3">
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
            <div className="mb-2 flex items-center gap-2">
              <TrendingUp className="h-4 w-4 shrink-0 text-amber-600" />
              <p className="text-xs font-bold text-gray-900">
                {findMoreContactCount.toLocaleString()} more owner email{findMoreContactCount !== 1 ? 's' : ''} are waiting
              </p>
            </div>
            <p className="text-[11px] text-gray-500">
              You&apos;re out of credits · upgrade to keep finding across {findMoreCompanyCount} {findMoreCompanyCount === 1 ? 'company' : 'companies'}
            </p>
          </div>
          <Button onClick={onUpgradeClick} variant="primary" className="w-full">
            Upgrade
          </Button>
          <button
            type="button"
            onClick={onClose}
            className="w-full py-1.5 text-sm font-medium text-gray-400 transition-colors hover:text-gray-600"
          >
            Maybe later
          </button>
        </div>
      )}

      {/* Done button — only when no nudge or loading */}
      {!hasAnyNudge && (
        <Button onClick={onClose} variant="primary" className="w-full">
          Done
        </Button>
      )}
    </div>
  );
};

export default EnrichmentResults;
